import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { AdditionalModule, ImplementationPackage, BillingData, PaymentMethod, SubscriptionData, SubscriptionPackage } from '../BillingTypes';
import { calculateAlreadyPaidAmount, calculateCurrentSubscriptionPeriodFraction, calculateImplementationPackagePrice, calculateNewSubscriptionAmount, getNewSubscriptionSeatPrice, getNextInvoiceDate, formatPriceOnUserLocale, getModuleMonthlyPriceForSeats, getTotalEnteredSeats } from '../functions/billingUtils';
import { formatDate } from 'services/utils/dateTimeUtils';
import '../../../../style/scss/billing.scss';

interface SubscriptionOverviewProps {
    packages: SubscriptionPackage[];
    additionalModules: AdditionalModule[];
    implementationPackages?: ImplementationPackage[];
    currentSubscription: SubscriptionData;
    updatedSubscription: SubscriptionData;
    paymentMethodLoading: string;
    billingData: BillingData;
    paymentMethodData: PaymentMethod;
    refetchPaymentMethod: () => void;
}

const SubscriptionOverview: React.FC<SubscriptionOverviewProps> = ({
    packages, additionalModules, implementationPackages, currentSubscription, updatedSubscription, paymentMethodData
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();

    // Find the selected package
    const selectedPackageObject = packages.find(pkg => pkg.key === updatedSubscription.package);

    // Determine if the updated subscription has additional modules
    const hasAdditionalModules = Array.isArray(updatedSubscription.additional_modules)
        ? updatedSubscription.additional_modules.filter(module => module.name?.trim())
        : [];

    // Get the monthly price of an additional module for the entered seats
    const getModuleMonthlyPrice = (moduleName: string) => {

        // Get the additional module option
        const moduleOption = additionalModules.find(module => module.feature === moduleName && module.is_active);

        // Return an empty price when the module option is not found
        if (!moduleOption) {
            return '';
        }

        // Return the monthly module price for the total entered seats
        const totalSeats = getTotalEnteredSeats(updatedSubscription);
        return formatPriceOnUserLocale(getModuleMonthlyPriceForSeats(moduleOption, totalSeats), userLocale);
    };

    // Determine if the updated subscription is an upgrade
    const isUpgrade = () => {

        // Don't show if this is a new subscriptions made from a trial
        if (currentSubscription.is_trial) {
            return false;
        };
        
        // Handle duration change from yearly to monthly as downgrade
        if (currentSubscription.duration === 'yearly' && updatedSubscription.duration === 'monthly') {
            return false;
        };

        // Get the old and new package from the package options
        const oldPackage = packages.find(pkg => pkg.key === currentSubscription.package);
        const newPackage = packages.find(pkg => pkg.key === updatedSubscription.package);

        if (oldPackage && newPackage && (oldPackage !== newPackage)) {
            // Determine if its an upgrade based on the standard monthly price, like the backend does
            const isUpgrade = Number(newPackage.standard_monthly_price ?? newPackage.monthly_price) > Number(oldPackage.standard_monthly_price ?? oldPackage.monthly_price);

            return isUpgrade;
        };

        return false;
    };

    // Determine if the updated subscription is a seats or modules upgrade
    const isSeatsOrModulesUpgrade = () => {

        // Return false if this is a trial
        if (currentSubscription.is_trial) {
            return false;
        };
        
        // Check for seats or modules upgrade if the package stays the same
        if (updatedSubscription.package === currentSubscription.package) {

            // Get the seats of the current subscription
            const oldNormalSeats = Number(currentSubscription.seats.normal);
            const oldFieldSeats = Number(currentSubscription.seats.field);
            const oldLightSeats = Number(currentSubscription.seats.light);

            // Get the seats of the new subscription
            const newNormalSeats = Number(updatedSubscription.seats.normal);
            const newFieldSeats = Number(updatedSubscription.seats.field);
            const newLightSeats = Number(updatedSubscription.seats.light);

            // Handle as seats or modules upgrade if new normal seats are higher
            if (newNormalSeats > oldNormalSeats) {
                return true;
            };

            // Handle as seats or modules upgrade if new field seats are higher
            if (newFieldSeats && newFieldSeats > (oldFieldSeats || 0)) {
                return true;
            };

            // Handle as seats or modules upgrade if new light seats are higher
            if (newLightSeats && newLightSeats > (oldLightSeats || 0)) {
                return true;
            };

            // Get the additional modules names of the current subscription
            const currentAdditionalModuleNames = (currentSubscription.subscription_lines || [])
                .filter(line => line.type === "additional_module" && line.is_active)
                .map(line => line.product_name)
                .filter((name): name is string => !!name);

            // Get the additional modules names of the new subscription
            const newAdditionalModuleNames = (updatedSubscription.additional_modules || [])
                .map(module => module.name)
                .filter((name): name is string => !!name);

            // Handle as seats or modules upgrade if new modules are added
            if (newAdditionalModuleNames.some(name => !currentAdditionalModuleNames.includes(name))) {
                return true
            };
        }

        return false;
    };

    // Calculate the remaining upgrade amount to pay
    const calculateRemainingUpgradeAmount = (
        updatedSubscription: SubscriptionData, 
        currentSubscription: SubscriptionData, 
        userLocale: string,
    ) => {

        // Calculate the upgraded subscription amount
        let upgradedSubscriptionAmount = Number(calculateNewSubscriptionAmount({
            packages,
            additionalModules,
            updatedSubscription: updatedSubscription, 
            returnAmount: 'totalInclVat',
            userLocale,
            returnAsNumber: true
        }))

        // For upgrades, calculate the remaining fraction of the subscription
        let remainingFraction = 1;
        if ((isUpgrade() || isSeatsOrModulesUpgrade()) && currentSubscription.end_date) {
            remainingFraction = calculateCurrentSubscriptionPeriodFraction(currentSubscription.end_date, currentSubscription.duration);

            // Calculate the remaining amount of the upgraded subscription based on the fraction
            upgradedSubscriptionAmount = upgradedSubscriptionAmount * remainingFraction;
        };

        // Check wheter the total has decimals. If the amount has one decimal, convert it to two decimals
        const hasDecimals = upgradedSubscriptionAmount % 1 !== 0;
        const decimalPlaces = hasDecimals
            ? (upgradedSubscriptionAmount.toString().split('.')[1]?.length === 1 ? 2 : 2)
            : 0;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(upgradedSubscriptionAmount);

        return formattedAmount;
    };

    // Calculate total to pay
    const calculateTotalToPay = (
        updatedSubscription: SubscriptionData, 
        currentSubscription: SubscriptionData, 
        userLocale: string,
    ) => {
        // Calculate the new subscription amount
        let newSubscriptionAmount = Number(calculateNewSubscriptionAmount({
            packages,
            additionalModules,
            updatedSubscription, 
            returnAmount: 'totalInclVat',
            userLocale,
            returnAsNumber: true
        }));

        // For upgrades, calculate the remaining fraction of the subscription
        let remainingFraction = 1;
        if ((isUpgrade() || isSeatsOrModulesUpgrade()) && currentSubscription.end_date) {
            remainingFraction = calculateCurrentSubscriptionPeriodFraction(currentSubscription.end_date, currentSubscription.duration);

            // Calculate the remaining amount of the upgraded subscription based on the fraction
            newSubscriptionAmount = newSubscriptionAmount * remainingFraction;
        };

        // Calculate the implementation package price
        let implementationPackageAmount = 0;
        if (updatedSubscription.implementation_package) {
            implementationPackageAmount = Number(calculateImplementationPackagePrice({
                implementationPackage: updatedSubscription.implementation_package,
                implementationPackages: implementationPackages || [],
                returnAmount: 'totalInclVat',
                userLocale,
                returnAsNumber: true
            }));
        };

        // Calculate the already paid amount
        let alreadyPaid = 0;
        if (isUpgrade() || isSeatsOrModulesUpgrade()) {
            alreadyPaid = Number(calculateAlreadyPaidAmount(currentSubscription, userLocale, true));
        };

        // Calculate the total to pay
        const totalToPay = (newSubscriptionAmount + implementationPackageAmount) - alreadyPaid;

        // Check wheter the total has decimals. If the amount has one decimal, convert it to two decimals
        const hasDecimals = totalToPay % 1 !== 0;
        const decimalPlaces = hasDecimals
            ? (totalToPay.toString().split('.')[1]?.length === 1 ? 2 : 2)
            : 0;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        }).format(totalToPay);

        return formattedAmount; 
    };

    return (
        <div className='wizard-step subscription-overview'>
            <div className='header-title'>
                <h2>{t('billing.general.billing_overview_header')}</h2>
            </div>
            <div className='step-content'>
                <div className='subscription-overview-grid'>
                    <div>
                        <div className='card subscription-details'>
                            <h3>
                                {t('billing.general.your_subscription_header')}
                            </h3>
                            <div className='subscription-details-content'>
                                <div>
                                    <div>
                                        <div>
                                            {selectedPackageObject && (
                                                <p>
                                                    {t('billing.general.current_package_label', { currentPackage: selectedPackageObject.name } )}
                                                </p>    
                                            )}
                                            <p>
                                                {t('billing.general.current_normal_seats_and_price_label', { 
                                                    normalSeats: parseInt(updatedSubscription.seats.normal), 
                                                    currentNormalSeatPrice: getNewSubscriptionSeatPrice('normal_seat', packages, updatedSubscription, userLocale) 
                                                })}
                                            </p>
                                            {updatedSubscription.seats.field && parseInt(updatedSubscription.seats.field) > 0 && (
                                                <p>
                                                    {t('billing.general.current_field_seats_and_price_label', { 
                                                        fieldSeats: parseInt(updatedSubscription.seats.field), 
                                                        currentFieldSeatPrice: getNewSubscriptionSeatPrice('field_seat', packages, updatedSubscription, userLocale) 
                                                    })}
                                                </p>
                                            )}
                                            {updatedSubscription.seats.light && parseInt(updatedSubscription.seats.light) > 0 && (
                                                <p>
                                                    {t('billing.general.current_light_seats_and_price_label', { 
                                                        lightSeats: parseInt(updatedSubscription.seats.light), 
                                                        currentLightSeatPrice: getNewSubscriptionSeatPrice('light_seat', packages, updatedSubscription, userLocale) 
                                                    })}
                                                </p>
                                            )}
                                            {(updatedSubscription.duration === 'yearly' || updatedSubscription.duration === 'monthly') && (
                                                <p>{t(`billing.general.${updatedSubscription.duration}_payment_label`)}</p>
                                            )}
                                        </div>

                                        {/* Additional modules */}
                                        {hasAdditionalModules.length > 0 && (
                                            <div className='module-details'>
                                                <h6>{t('billing.general.additional_modules_label')}</h6>
                                                {hasAdditionalModules.map((mod, index) => (
                                                    <p key={index}>
                                                        {t('billing.general.additional_module_line_label', {
                                                            productName: t(`billing.feature.${mod.name}`, { defaultValue: mod.name }),
                                                            modulePrice: getModuleMonthlyPrice(mod.name)
                                                        })}
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className='recurring-price'>
                                    <div className='price-big'>
                                        <span className='currency-icon'>€ </span>
                                        {calculateNewSubscriptionAmount({
                                            packages,
                                            additionalModules,
                                            updatedSubscription, 
                                            displayPrice: updatedSubscription.duration,
                                            returnAmount: 'totalExclVat',
                                            userLocale
                                        })}
                                    </div>
                                    {updatedSubscription.duration === 'yearly' && (
                                        t(`billing.general.per_year_label`)
                                    )}
                                    {updatedSubscription.duration === 'monthly' && (
                                        t(`billing.general.per_month_label`)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className='card payment-summary'>
                            {currentSubscription.is_trial ? (
                                <h3>{t('billing.general.complete_new_subscription_header')}</h3>
                            ) : (
                                <h3>{t('billing.general.complete_changed_subscription_header')}</h3>
                            )}
                            {currentSubscription.is_trial ? (
                                <p>
                                    {t(`billing.general.complete_new_subscription_by_${paymentMethodData.payment_type}_description`, { 
                                        duration_label: t(`billing.general.${updatedSubscription.duration}_label`) 
                                    })}
                                </p>
                            ) : (
                                isUpgrade() || isSeatsOrModulesUpgrade() ? (
                                    <p>
                                        {t(`billing.general.complete_upgraded_subscription_by_${paymentMethodData.payment_type}_description`)}
                                    </p>
                                ) : (
                                    <p>
                                        {t('billing.general.complete_downgraded_subscription_description')}
                                    </p>
                                )
                            )}
                            <div className='payment-details'>
                                <div className='price-detail-section'>
                                    <div className='price-detail-row'>
                                        <p>
                                            {t('billing.general.subtotal_label')}
                                        </p>
                                        <p className='price-amount'>
                                            <span className='currency-icon'>€ </span>
                                            {calculateNewSubscriptionAmount({
                                                packages,
                                                additionalModules,
                                                updatedSubscription: updatedSubscription, 
                                                returnAmount: 'totalExclVat',
                                                userLocale
                                            })}
                                        </p>
                                    </div>
                                    <div className='price-detail-row'>
                                        <p>
                                            {t('billing.general.vat_label', { vatrate: 21 })}
                                        </p>
                                        <p className='price-amount'>
                                            <span className='currency-icon'>€ </span>
                                            {calculateNewSubscriptionAmount({
                                                packages,
                                                additionalModules,
                                                updatedSubscription: updatedSubscription, 
                                                returnAmount: 'vatOnly',
                                                userLocale
                                            })}
                                        </p>
                                    </div>
                                    <div className='price-detail-row'>
                                        <p className='bold'>
                                            {t('billing.general.total_recurring_period_payment', { 
                                                duration_term: t(`billing.general.${updatedSubscription.duration}_label`) 
                                            })}
                                        </p>
                                        <p className='bold price-amount'>
                                            <span className='currency-icon'>€ </span>
                                            {calculateNewSubscriptionAmount({
                                                packages,
                                                additionalModules,
                                                updatedSubscription: updatedSubscription, 
                                                returnAmount: 'totalInclVat',
                                                userLocale
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {updatedSubscription.implementation_package && (
                                    <div className='price-detail-section'>
                                        <div className='price-detail-row'>
                                            <p>
                                                {t('billing.general.implementation_costs_label')}
                                            </p>
                                            <p className='price-amount'>
                                                <span className='currency-icon'>€ </span>
                                                {calculateImplementationPackagePrice({
                                                    implementationPackage: updatedSubscription.implementation_package,
                                                    implementationPackages: implementationPackages || [],
                                                    returnAmount: 'totalExclVat',
                                                    userLocale
                                                })}
                                            </p>
                                        </div>
                                        <div className='price-detail-row'>
                                            <p>
                                                {t('billing.general.vat_label', { vatrate: 21 })}
                                            </p>
                                            <p className='price-amount'>
                                                <span className='currency-icon'>€ </span>
                                                {calculateImplementationPackagePrice({
                                                    implementationPackage: updatedSubscription.implementation_package,
                                                    implementationPackages: implementationPackages || [],
                                                    returnAmount: 'vatOnly',
                                                    userLocale
                                                })}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <div className='price-detail-section' style={{ marginBottom: 'unset' }}>
                                        {currentSubscription.is_trial ? (
                                            <div className='price-detail-row'>
                                                <p style={{ marginBottom: 'unset' }}>
                                                    {t('billing.general.to_pay_today_label')}
                                                </p>
                                                <p className='price-amount'>
                                                    <span className='currency-icon'>€ </span>
                                                    {calculateTotalToPay(updatedSubscription, currentSubscription, userLocale)}
                                                </p>
                                            </div> 
                                        ) : (
                                            isUpgrade() || isSeatsOrModulesUpgrade() ? (
                                                <div>
                                                    <div className='price-detail-row'>
                                                        <p>
                                                            {t('billing.general.to_pay_for_upgrade_label')}
                                                        </p>
                                                        <p className='price-amount'>
                                                            <span className='currency-icon'>€ </span>
                                                            {calculateRemainingUpgradeAmount(updatedSubscription, currentSubscription, userLocale)}
                                                        </p>
                                                    </div>
                                                    <div className='price-detail-row'>
                                                        <p>
                                                            {t('billing.general.already_paid_correction_label', { date: formatDate(currentSubscription.start_date!, userLocale) })}
                                                        </p>
                                                        <p className='price-amount'>
                                                            <span className='currency-icon'>-€ </span>
                                                            {calculateAlreadyPaidAmount(currentSubscription, userLocale)}
                                                        </p>
                                                    </div>
                                                    <div className='price-detail-row'>
                                                        <p className='bold' style={{ marginBottom: 'unset' }}>
                                                            {t('billing.general.to_pay_today_label')}
                                                        </p>
                                                        <p className='bold price-amount'>
                                                            <span className='currency-icon'>€ </span>
                                                            {calculateTotalToPay(updatedSubscription, currentSubscription, userLocale)}
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='price-detail-row'>
                                                    <p style={{ marginBottom: 'unset' }}>
                                                        {t('billing.general.next_payment_date_label', { 
                                                            next_invoice_date: getNextInvoiceDate(updatedSubscription, userLocale) 
                                                        })}
                                                    </p>
                                                </div> 
                                            )
                                        )}
                                </div>
                            </div>     
                        </div>                
                    </div>    
                </div>
            </div>
        </div>
    )
}

export default SubscriptionOverview;